<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CreateToolSetForm from './CreateToolSetForm.vue';

const props = defineProps({
  botId: {
    type: Number,
    required: true,
  },
  tool: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const isOpen = ref(false);
const dialogRef = ref(null);
const formRef = ref(null);

const uiFlags = useMapGetter('wgptBotTools/getUIFlags');

const handleSubmit = async () => {
  if (!formRef.value) return;
  const { state, validate } = formRef.value;

  try {
    const isFormValid = await validate();
    if (!isFormValid) return;

    const createOptions = {
      botId: props.botId,
      ...state,
    };
    await store.dispatch('wgptBotTools/create', createOptions);

    const successMessage = t(
      'WGPT_BOTS.EDIT.TABS.TOOLS.CREATE_TOOL_SET.API.SUCCESS_MESSAGE'
    );
    useAlert(successMessage);
    dialogRef.value.close();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('WGPT_BOTS.EDIT.TABS.TOOLS.CREATE_TOOL_SET.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleOpen = () => {
  isOpen.value = true;
};

const handleDismiss = () => {
  isOpen.value = false;
  emit('close');
};

const dismiss = () => {
  dialogRef.value.close();
  handleDismiss();
};

const open = () => {
  handleOpen();
  nextTick(() => {
    dialogRef.value?.open();
  });
};

defineExpose({ open, dismiss });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('WGPT_BOTS.EDIT.TABS.TOOLS.CREATE_TOOL_SET.TITLE')"
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    :confirm-button-label="
      t('WGPT_BOTS.EDIT.TABS.TOOLS.CREATE_TOOL_SET.FORM.SAVE')
    "
    :cancel-button-label="
      t('WGPT_BOTS.EDIT.TABS.TOOLS.CREATE_TOOL_SET.FORM.CANCEL')
    "
    overflow-y-auto
    @confirm="handleSubmit"
    @close="handleDismiss"
  >
    <CreateToolSetForm
      v-if="isOpen"
      ref="formRef"
      :tool="tool"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
